import { useState } from 'react';
import { adminApi, ApiError } from '../lib/api';
import Eyebrow from './Eyebrow';
import Button from './Button';

interface Props {
  scriptId: string;
  currentEmail: string | null;
  /** Called after a successful save so the parent can refresh the loaded
   *  script without refetching. */
  onSaved: (next: string | null) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Set (or clear) this script's "ready to review" nudge target (Phase B3).
 * Unlike DashboardSlugPanel, this is script-scoped — one script, one
 * contact — so no cross-script warning copy is needed here. Publishing
 * this script (leaving draft) will email this address once, provided the
 * parent project also has a dashboard_slug set.
 */
export default function ClientEmailPanel({ scriptId, currentEmail, onSaved }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(currentEmail ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function startEdit() {
    setDraft(currentEmail ?? '');
    setError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setError(null);
    setDraft(currentEmail ?? '');
  }

  async function handleSave() {
    const trimmed = draft.trim();
    const next: string | null = trimmed === '' ? null : trimmed;
    if (next !== null && !EMAIL_RE.test(next)) {
      setError('Enter a valid email address, or leave blank to clear.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await adminApi.setScriptClientEmail(scriptId, next);
      onSaved(res.client_email);
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      data-print-hide
      className="bg-card border border-line rounded-md px-5 py-4"
    >
      <div className="flex items-center justify-between mb-3">
        <Eyebrow>// Client email</Eyebrow>
        {!editing && (
          <button
            onClick={startEdit}
            className="
              font-mono text-[10px] uppercase tracking-[0.12em]
              text-muted hover:text-fg transition-colors
            "
          >
            {currentEmail ? 'Edit' : 'Set'}
          </button>
        )}
      </div>

      {editing ? (
        <div>
          <input
            type="email"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleSave();
              if (e.key === 'Escape') cancelEdit();
            }}
            placeholder="client@example.com"
            disabled={saving}
            autoFocus
            className="
              w-full font-mono text-[13px]
              bg-page border border-line rounded px-3 py-2 text-fg
              focus:outline-none focus:border-accent transition-colors
              disabled:opacity-50
            "
          />
          {error && (
            <p className="mt-2 text-accent text-[12px]">{error}</p>
          )}
          <p className="mt-2 text-faint text-[11px] leading-relaxed">
            Optional. Leave blank to clear.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={cancelEdit}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : currentEmail ? (
        <div>
          <p className="font-mono text-[13px] text-fg bg-page/60 border border-line rounded px-3 py-2 truncate">
            {currentEmail}
          </p>
          <p className="mt-2 text-faint text-[11px]">
            Emailed "ready to review" once this script first leaves Draft
            (only if the project also has a dashboard slug).
          </p>
        </div>
      ) : (
        <p className="text-muted text-[13px] leading-relaxed">
          No email set. Publishing this script won't send a client nudge
          until you add one.
        </p>
      )}
    </section>
  );
}
